import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet, apiPut, apiPost } from "./client";

/**
 * /api/v2/tenant/settings*, /api/v2/tenant/credentials, /connection-test —
 * sozlesme §5 (tenant_admin). Ayar sozlugu (§2) noktali-anahtar → JSONB
 * degeri; tipler karisik (str/int/bool/obj/list) oldugundan iskelede
 * z.record(string, unknown) ile tasinir — /ayarlar sayfasi (P5-F4) alan
 * bazinda daraltir.
 */

export const tenantSettingsSchema = z.record(z.string(), z.unknown());
export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

/**
 * GET /api/v2/tenant/public-settings — Faz 5 P5-F4 (bkz. gorev talimati
 * madde 1). Backend entegrasyon paketi ekleyecek; HENUZ yok (404 beklenir,
 * bu durumda TenantTheme sessizce varsayilan temaya duser — bkz. `enabled`
 * kosulu asagida ve TenantTheme.tsx). Ayni dotted-key sozlesmesini
 * (§2) korur ama yalniz HERKESE guvenli alt kume ile sinirlidir: brand.*,
 * ui.hidden_cards/ui.hidden_tabs/ui.language, alarm.*, ai.enabled — bu
 * yuzden `/tenant/settings` ile ayni z.record semasi yeniden kullanilir.
 */
export const publicSettingsSchema = z.record(z.string(), z.unknown());
export type PublicSettings = z.infer<typeof publicSettingsSchema>;

export const credentialKeyInfoSchema = z.object({
  key: z.string(),
  updated_at: z.string().nullable(),
  expires_at: z.string().nullable(),
  masked: z.string(),
});
export type CredentialKeyInfo = z.infer<typeof credentialKeyInfoSchema>;

/** GET/PUT /api/v2/tenant/credentials her ikisi de `{keys:[...]}` doner
 * (app/core/api/settings.py) — DEGER hicbir zaman yaniti girmez. */
export const credentialKeysResponseSchema = z.object({ keys: z.array(credentialKeyInfoSchema) });
export type CredentialKeysResponse = z.infer<typeof credentialKeysResponseSchema>;

export const connectionTestResponseSchema = z.object({ job_id: z.number().nullable() });

export function fetchTenantSettings(): Promise<TenantSettings> {
  return apiGet("/api/v2/tenant/settings", tenantSettingsSchema);
}

export function fetchPublicSettings(): Promise<PublicSettings> {
  return apiGet("/api/v2/tenant/public-settings", publicSettingsSchema);
}

export function fetchTenantSettingsDefaults(): Promise<TenantSettings> {
  return apiGet("/api/v2/tenant/settings/defaults", tenantSettingsSchema);
}

export function updateTenantSettingsRequest(values: Record<string, unknown>): Promise<TenantSettings> {
  // Backend PutSettingsRequest `{values: {...}}` sarmalayicisi bekler
  // (app/core/api/settings.py:47-48) — duz obje govdesi 422 doner.
  return apiPut("/api/v2/tenant/settings", tenantSettingsSchema, { values });
}

export function fetchTenantCredentials(): Promise<CredentialKeyInfo[]> {
  return apiGet("/api/v2/tenant/credentials", credentialKeysResponseSchema).then((res) => res.keys);
}

export function updateTenantCredentialsRequest(values: Record<string, string>): Promise<CredentialKeyInfo[]> {
  // Backend PutCredentialsRequest de ayni `{values: {...}}` sarmalayicisini
  // bekler (app/core/api/settings.py:51-52).
  return apiPut("/api/v2/tenant/credentials", credentialKeysResponseSchema, { values }).then((res) => res.keys);
}

export function connectionTestRequest() {
  return apiPost("/api/v2/tenant/connection-test", connectionTestResponseSchema);
}

// ---------------------------------------------------------------------------
// Kancalar
// ---------------------------------------------------------------------------

export function useTenantSettingsQuery(enabled = true) {
  return useQuery({ queryKey: ["tenant", "settings"], queryFn: fetchTenantSettings, enabled });
}

/** Her tenant_user icin (rol sarti yok) — bkz. TenantTheme.tsx. 404/hata →
 * sessizce varsayilan temaya duser (retry yok, hata arayuzde gosterilmez). */
export function usePublicSettingsQuery(enabled = true) {
  return useQuery({
    queryKey: ["tenant", "public-settings"],
    queryFn: fetchPublicSettings,
    enabled,
    retry: false,
    staleTime: 60_000,
  });
}

export function useTenantSettingsDefaultsQuery() {
  return useQuery({ queryKey: ["tenant", "settings", "defaults"], queryFn: fetchTenantSettingsDefaults });
}

export function useUpdateTenantSettingsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTenantSettingsRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["tenant", "settings"] });
      // brand.*/ai.enabled degisiklikleri canli tema/nav'i etkiler (madde 1/6).
      void queryClient.invalidateQueries({ queryKey: ["tenant", "public-settings"] });
      void queryClient.invalidateQueries({ queryKey: ["cards"] });
    },
  });
}

export function useTenantCredentialsQuery() {
  return useQuery({ queryKey: ["tenant", "credentials"], queryFn: fetchTenantCredentials });
}

export function useUpdateTenantCredentialsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTenantCredentialsRequest,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["tenant", "credentials"] }),
  });
}

export function useConnectionTestMutation() {
  return useMutation({ mutationFn: connectionTestRequest });
}
